import Phaser from 'phaser';
import { BRAND, GAME, HEX } from '../config';
import { LEVELS, ITEMS, isInverter, isBattery, isPanel } from '../game/levels';
import type { Level } from '../game/levels';
import { Roof } from '../game/Roof';
import { Sun } from '../game/Sun';
import {
  PanelSprite,
  makePanel,
  panelOccupiedCells,
  type PanelInstance,
} from '../game/Panel';
import { HUD } from '../game/HUD';
import { formatTime, getBest } from '../game/scores';
import { presentLogical } from '../hidpi';

type SceneData = { levelIndex: number };

const BATTERY_SUN_PENALTY = 0.1; // 10% off total system output per battery placed in 'sun' ground

export class LevelScene extends Phaser.Scene {
  private level!: Level;
  private levelIndex = 0;

  private roof!: Roof;
  private sun!: Sun;
  private hud!: HUD;

  private placed: { panel: PanelInstance; sprite: PanelSprite }[] = [];
  private remaining!: Record<string, number>;
  private selectedType!: string;

  private ghost?: PanelSprite;
  private ghostFacing = 180;
  private ghostValid = false;

  private hintBg?: Phaser.GameObjects.Graphics;
  private hintText?: Phaser.GameObjects.Text;

  private wireGfx!: Phaser.GameObjects.Graphics;

  private startedAt = 0;
  private timerText!: Phaser.GameObjects.Text;

  // Hidden trade-pricing easter egg — type "JUICE" anywhere in the level for 25% off everything.
  private juiceTyped = '';
  private juiceMode = false;

  constructor() {
    super('LevelScene');
  }

  init(data: SceneData) {
    this.levelIndex = data.levelIndex ?? 0;
    this.level = LEVELS[this.levelIndex];
    this.placed = [];
    this.remaining = {};
    for (const item of this.level.inventory) {
      this.remaining[item.type] = item.count;
    }
    this.selectedType = this.level.inventory[0].type;
    this.ghost = undefined;
    // Panels start in portrait footprint; sun azimuth is unrelated to footprint.
    this.ghostFacing = 180;
    this.juiceTyped = '';
    this.juiceMode = false;
  }

  create() {
    const { width, height } = presentLogical(this);
    const sidebarW = 260;
    const playW = width - sidebarW;
    const playX = sidebarW;

    this.cameras.main.setBackgroundColor(BRAND.ink);

    const sky = this.add.graphics();
    sky.fillGradientStyle(BRAND.skyTop, BRAND.skyTop, BRAND.ink, BRAND.ink, 1, 1, 0, 0);
    sky.fillRect(playX, 0, playW, height);

    const sidebarBg = this.add.graphics();
    sidebarBg.fillStyle(BRAND.inkSoft, 1);
    sidebarBg.fillRect(0, 0, sidebarW, height);
    sidebarBg.fillStyle(BRAND.red, 1);
    sidebarBg.fillRect(sidebarW - 3, 0, 3, height);
    sidebarBg.fillStyle(0xffffff, 0.04);
    sidebarBg.fillRect(0, 0, 1, height);

    const sunCx = playX + playW - 90;
    const sunCy = 90;
    this.sun = new Sun(this, this.level.sunAzimuthDeg, { x: sunCx, y: sunCy }, 52);
    this.sun.draw();
    this.sun.startAnimations();

    // Roof bounding box in cells
    let maxW = 0;
    let maxH = 0;
    for (const face of this.level.faces) {
      for (const r of face.cells) {
        if (r.x + r.w > maxW) maxW = r.x + r.w;
        if (r.y + r.h > maxH) maxH = r.y + r.h;
      }
    }
    const roofPxW = maxW * GAME.cell;
    const roofPxH = maxH * GAME.cell;
    // Centre the roof in the play area.
    const roofX = playX + (playW - roofPxW) / 2;
    const roofY = 180 + ((height - 240) - roofPxH) / 2;

    this.roof = new Roof(this, this.level, { x: roofX, y: roofY });
    // Play area for ground placement: the right column of the canvas, with
    // a margin so we don't crowd the back button / top bar / controls strip.
    this.roof.setPlayArea(
      new Phaser.Geom.Rectangle(playX + 12, 56, playW - 24, height - 56 - 56),
    );
    this.roof.draw();

    // Wire layer (drawn between roof and placed sprites — depth tweaked)
    this.wireGfx = this.add.graphics().setDepth(1);

    this.hud = new HUD(
      this,
      this.level,
      new Phaser.Geom.Rectangle(0, 0, sidebarW, height),
      {
        onSelect: (id) => {
          this.selectedType = id;
        },
        onSubmit: () => this.submit(),
        onReset: () => this.scene.restart({ levelIndex: this.levelIndex }),
      },
    );
    this.hud.build();
    this.hud.update(this.computeState());

    this.makeBackButton(sidebarW + 14, 14);

    // Timer + best-time pill — single row, aligned with the back button
    this.startedAt = this.time.now;
    const tbH = 28;
    const tbY = 14;
    const best = getBest(this.level.id);
    const bestStr = best != null ? formatTime(best) : '—:—';
    // Measure text widths so the pill auto-sizes
    const measure = (s: string, size: string, weight = 'bold') => {
      const t = this.add
        .text(0, 0, s, { fontFamily: 'Albert Sans, sans-serif', fontSize: size, fontStyle: weight })
        .setVisible(false);
      const w = t.width;
      t.destroy();
      return w;
    };
    const labelGap = 6;
    const sectionGap = 16;
    const padX = 14;
    const tWidth = Math.max(48, measure('00:00', '15px'));
    const bWidth = Math.max(48, measure(bestStr, '15px'));
    const tbW = padX + measure('TIME', '10px') + labelGap + tWidth +
      sectionGap + measure('BEST', '10px') + labelGap + bWidth + padX;
    const tbX = playX + (playW - tbW) / 2;

    const tBg = this.add.graphics();
    tBg.fillStyle(BRAND.inkSoft, 0.9);
    tBg.fillRoundedRect(tbX, tbY, tbW, tbH, 6);
    tBg.lineStyle(1, BRAND.amber, 0.55);
    tBg.strokeRoundedRect(tbX + 0.5, tbY + 0.5, tbW - 1, tbH - 1, 6);

    let cx = tbX + padX;
    this.add
      .text(cx, tbY + tbH / 2, 'TIME', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(1.5);
    cx += measure('TIME', '10px') + labelGap;
    this.timerText = this.add
      .text(cx, tbY + tbH / 2, '00:00', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '15px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    cx += tWidth + sectionGap;
    // Divider tick
    tBg.fillStyle(0xffffff, 0.12);
    tBg.fillRect(cx - sectionGap / 2, tbY + 6, 1, tbH - 12);
    this.add
      .text(cx, tbY + tbH / 2, 'BEST', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(1.5);
    cx += measure('BEST', '10px') + labelGap;
    this.add
      .text(cx, tbY + tbH / 2, bestStr, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '15px',
        color: best != null ? HEX.white : HEX.greyDim,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    // Persistent controls strip at bottom of play area
    this.drawControlsStrip(playX + 16, height - 38, playW - 32, 28);

    this.hintBg = this.add.graphics().setDepth(1000).setVisible(false);
    this.hintText = this.add
      .text(0, 0, '', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setDepth(1001)
      .setVisible(false);

    this.cameras.main.fadeIn(280, 0x2a, 0x31, 0x40);

    this.input.mouse?.disableContextMenu();
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
    this.input.keyboard?.on('keydown-R', () => {
      this.ghostFacing = (this.ghostFacing + 90) % 360;
      this.refreshGhost();
    });
    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('MenuScene'));

    // Hidden cheat code — type the brand name JUICE to unlock trade pricing
    // (25% discount on all items). Only fires once per level.
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      if (this.juiceMode) return;
      const key = ev.key?.toUpperCase();
      if (!key || key.length !== 1) return;
      const target = 'JUICE';
      this.juiceTyped += key;
      // Only keep the suffix matching `target`
      if (!target.startsWith(this.juiceTyped)) {
        // Try shrinking from the front until what we have matches a prefix
        while (this.juiceTyped.length > 0 && !target.startsWith(this.juiceTyped)) {
          this.juiceTyped = this.juiceTyped.slice(1);
        }
      }
      if (this.juiceTyped === target) this.activateJuiceMode();
    });
  }

  private activateJuiceMode() {
    if (this.juiceMode) return;
    this.juiceMode = true;
    this.hud.update(this.computeState());
    // Pop a celebratory badge top-centre, just under the timer
    const cx = (GAME.width + 260) / 2; // centre of play area
    const badge = this.add.container(cx, 60);
    const bg = this.add.graphics();
    const label = '🍊  JUICE PRICING UNLOCKED  ·  −25%';
    const tmp = this.add
      .text(0, 0, label, { fontFamily: 'Albert Sans, sans-serif', fontSize: '13px', fontStyle: 'bold' })
      .setVisible(false);
    const w = tmp.width + 28;
    const h = 30;
    tmp.destroy();
    bg.fillStyle(BRAND.amber, 0.95);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.lineStyle(1, 0x000000, 0.4);
    bg.strokeRoundedRect(-w / 2 + 0.5, -h / 2 + 0.5, w - 1, h - 1, 6);
    badge.add(bg);
    const txt = this.add
      .text(0, 0, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '13px',
        color: '#1f2530',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    badge.add(txt);
    badge.setAlpha(0);
    badge.setScale(0.85);
    this.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 280,
      ease: 'Back.easeOut',
    });
    // Persist a small "JUICE" pip in top-right after the big badge fades
    this.tweens.add({
      targets: badge,
      alpha: 0,
      duration: 600,
      delay: 1800,
      onComplete: () => badge.destroy(),
    });
    this.makeJuicePip();
  }

  private makeJuicePip(): Phaser.GameObjects.Container {
    const padX = 8;
    const padY = 4;
    const label = '🍊 JUICE −25%';
    const tmp = this.add
      .text(0, 0, label, { fontFamily: 'Albert Sans, sans-serif', fontSize: '11px', fontStyle: 'bold' })
      .setVisible(false);
    const w = tmp.width + padX * 2;
    const h = tmp.height + padY * 2;
    tmp.destroy();
    const c = this.add.container(GAME.width - 16, 14);
    const bg = this.add.graphics();
    bg.fillStyle(BRAND.amber, 0.95);
    bg.fillRoundedRect(-w, 0, w, h, 4);
    c.add(bg);
    c.add(
      this.add
        .text(-w + padX, padY, label, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: '#1f2530',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0),
    );
    return c;
  }

  update() {
    if (!this.timerText) return;
    const ms = this.time.now - this.startedAt;
    this.timerText.setText(formatTime(ms));
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Controls strip — always-visible reference at the bottom of the play area
  // ────────────────────────────────────────────────────────────────────────────
  private drawControlsStrip(x: number, y: number, w: number, h: number) {
    const items: { key: string; label: string }[] = [
      { key: 'CLICK', label: 'Place' },
      { key: 'R', label: 'Rotate' },
      { key: 'SHIFT+CLICK', label: 'Remove' },
      { key: 'HOVER', label: 'Sun %' },
      { key: 'ESC', label: 'Menu' },
    ];
    const bg = this.add.graphics();
    bg.fillStyle(BRAND.inkSoft, 0.85);
    bg.fillRoundedRect(x, y, w, h, 6);
    bg.lineStyle(1, 0xffffff, 0.07);
    bg.strokeRoundedRect(x + 0.5, y + 0.5, w - 1, h - 1, 6);

    // Lay out left → right with consistent spacing
    let cx = x + 12;
    items.forEach((it, i) => {
      if (i > 0) {
        bg.fillStyle(0xffffff, 0.07);
        bg.fillRect(cx - 6, y + 6, 1, h - 12);
        cx += 6;
      }
      const chipW = this.drawKeyChip(cx, y + h / 2, it.key);
      cx += chipW + 6;
      const t = this.add
        .text(cx, y + h / 2, it.label, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: HEX.greyDim,
        })
        .setOrigin(0, 0.5);
      cx += t.width + 14;
    });
  }

  private drawKeyChip(x: number, cy: number, label: string): number {
    const padX = 6;
    const tmp = this.add
      .text(0, 0, label, { fontFamily: 'Albert Sans, sans-serif', fontSize: '10px' })
      .setVisible(false);
    const w = tmp.width + padX * 2;
    const h = 16;
    tmp.destroy();
    const g = this.add.graphics();
    g.fillStyle(0x141821, 1);
    g.fillRoundedRect(x, cy - h / 2, w, h, 3);
    g.lineStyle(1, BRAND.amber, 0.55);
    g.strokeRoundedRect(x + 0.5, cy - h / 2 + 0.5, w - 1, h - 1, 3);
    this.add
      .text(x + w / 2, cy, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(1);
    return w;
  }

  private makeBackButton(x: number, y: number): void {
    const w = 86;
    const h = 28;
    const c = this.add.container(x, y);
    const bg = this.add.graphics();
    const paint = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(BRAND.inkSoft, hover ? 1 : 0.85);
      bg.fillRoundedRect(0, 0, w, h, 6);
      bg.lineStyle(1, hover ? BRAND.amber : 0xffffff, hover ? 1 : 0.15);
      bg.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, 6);
    };
    paint(false);
    c.add(bg);
    const t = this.add
      .text(w / 2, h / 2, '← MENU', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(1.5);
    c.add(t);
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerover', () => paint(true));
    bg.on('pointerout', () => paint(false));
    bg.on('pointerdown', () => this.scene.start('MenuScene'));
    void c;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Pointer logic
  // ────────────────────────────────────────────────────────────────────────────
  private onPointerMove(pointer: Phaser.Input.Pointer) {
    const cell = this.roof.worldToCell(pointer.worldX, pointer.worldY);
    if (!cell) {
      this.clearGhost();
      this.hideHint();
      return;
    }

    this.showHint(pointer.worldX, pointer.worldY, cell);

    if ((this.remaining[this.selectedType] ?? 0) <= 0) {
      this.clearGhost();
      return;
    }
    const t = ITEMS[this.selectedType];
    const ghostPanel = makePanel(t, cell.x, cell.y, this.ghostFacing);
    const occupied = this.occupiedSet();
    const role = isPanel(t) ? 'panel' : isInverter(t) ? 'inverter' : 'battery';
    const valid = this.roof.canPlace(
      role,
      ghostPanel.cellX,
      ghostPanel.cellY,
      ghostPanel.spanW(),
      ghostPanel.spanH(),
      occupied,
    );
    this.ghostValid = valid;

    if (!this.ghost) {
      this.ghost = new PanelSprite(this, ghostPanel, this.roof.origin);
      this.ghost.draw();
    } else {
      this.ghost.panel = ghostPanel;
      this.ghost.refresh();
    }
    this.ghost.container.setAlpha(valid ? 0.78 : 0.4);
    if (!valid) {
      const overlay = this.ghost.body;
      overlay.fillStyle(BRAND.red, 0.28);
      const px = this.roof.origin.x + ghostPanel.cellX * GAME.cell;
      const py = this.roof.origin.y + ghostPanel.cellY * GAME.cell;
      overlay.fillRect(px, py, ghostPanel.spanW() * GAME.cell, ghostPanel.spanH() * GAME.cell);
    }
  }

  private showHint(x: number, y: number, cell: { x: number; y: number }) {
    if (!this.hintBg || !this.hintText) return;
    const zone = this.roof.zoneAt(cell.x, cell.y);
    if (!zone) {
      this.hideHint();
      return;
    }
    const t = ITEMS[this.selectedType];
    const role = t && (isPanel(t) ? 'panel' : isInverter(t) ? 'inverter' : 'battery');
    const lines: string[] = [];
    let warn = false;
    if (zone.kind === 'roof') {
      const cardinal = this.cardinalLabel(zone.pitch);
      const pct = Math.round(zone.multiplier * 100);
      if (zone.blocked) {
        lines.push(`${cardinal} face · BLOCKED`);
        lines.push('obstacle here');
        warn = true;
      } else if (role && role !== 'panel') {
        lines.push(`${cardinal} face · ${pct}% sun`);
        lines.push(`${t!.name} → place on the GROUND, not roof`);
        warn = true;
      } else {
        lines.push(`${cardinal} face · ${pct}% sun`);
        if (t && isPanel(t)) {
          lines.push(`${t.name} → ${Math.round((t.watts ?? 0) * zone.multiplier)} W`);
        }
      }
    } else {
      // Ground (anywhere off-roof, inside the play area)
      const sunPct = Math.round(zone.exposure * 100);
      const distLabel =
        zone.distanceFromRoof <= 1
          ? 'next to roof'
          : zone.distanceFromRoof <= 2
            ? `${zone.distanceFromRoof} cells from roof`
            : `${zone.distanceFromRoof} cells away (long cable run)`;
      const sunLabel =
        zone.exposure < 0.3
          ? 'shaded'
          : zone.exposure > 0.7
            ? 'DIRECT SUN'
            : `${sunPct}% sun`;
      lines.push(`Ground · ${sunLabel} · ${distLabel}`);
      if (role === 'panel') {
        lines.push(`${t!.name} → place on the ROOF, not ground`);
        warn = true;
      } else if (role === 'inverter') {
        lines.push(`${t!.name} — caps system at ${t!.capacityKW} kW`);
      } else if (role === 'battery') {
        const credit = (t!.bonusKW ?? 0) * (1 - zone.exposure);
        const penalty = zone.exposure * 100;
        if (zone.exposure > 0.7) {
          lines.push(`${t!.name} — overheats! −${penalty.toFixed(0)}% penalty`);
          warn = true;
        } else {
          lines.push(`${t!.name} — credit +${credit.toFixed(2)} kW`);
        }
      }
    }
    this.hintText.setText(lines.join('\n'));
    const padX = 8;
    const padY = 6;
    const tw = this.hintText.width + padX * 2;
    const th = this.hintText.height + padY * 2;
    let bx = x + 16;
    let by = y + 16;
    const maxX = GAME.width - tw - 4;
    const maxY = GAME.height - th - 4;
    if (bx > maxX) bx = x - tw - 16;
    if (by > maxY) by = y - th - 16;
    this.hintBg.clear();
    this.hintBg.fillStyle(0x141821, 0.94);
    this.hintBg.fillRoundedRect(bx, by, tw, th, 4);
    this.hintBg.lineStyle(1, warn ? BRAND.red : BRAND.amber, 0.9);
    this.hintBg.strokeRoundedRect(bx + 0.5, by + 0.5, tw - 1, th - 1, 4);
    this.hintText.setPosition(bx + padX, by + padY);
    this.hintBg.setVisible(true);
    this.hintText.setVisible(true);
  }

  private hideHint() {
    this.hintBg?.setVisible(false);
    this.hintText?.setVisible(false);
  }

  private cardinalLabel(deg: number): string {
    const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return names[Math.round(deg / 45) % 8];
  }

  private refreshGhost() {
    const p = this.input.activePointer;
    this.onPointerMove(p);
  }

  private clearGhost() {
    if (this.ghost) {
      this.ghost.destroy();
      this.ghost = undefined;
    }
  }

  private onPointerDown(pointer: Phaser.Input.Pointer) {
    if (pointer.rightButtonDown() || pointer.event.shiftKey) {
      this.tryRemoveAt(pointer.worldX, pointer.worldY);
      return;
    }
    if (!this.ghost || !this.ghostValid) return;
    if ((this.remaining[this.selectedType] ?? 0) <= 0) return;
    const placed = makePanel(
      this.ghost.panel.type,
      this.ghost.panel.cellX,
      this.ghost.panel.cellY,
      this.ghost.panel.facingDeg,
    );
    const sprite = new PanelSprite(this, placed, this.roof.origin);
    sprite.draw();
    this.placed.push({ panel: placed, sprite });
    this.remaining[placed.type.id]--;
    this.hud.update(this.computeState());
    this.redrawWires();

    sprite.container.setAlpha(0);
    this.tweens.add({
      targets: sprite.container,
      alpha: 1,
      duration: 180,
      ease: 'Sine.easeOut',
    });
    const flash = this.add.graphics();
    const fx = this.roof.origin.x + placed.cellX * GAME.cell;
    const fy = this.roof.origin.y + placed.cellY * GAME.cell;
    flash.fillStyle(BRAND.amber, 0.55);
    flash.fillRoundedRect(fx, fy, placed.spanW() * GAME.cell, placed.spanH() * GAME.cell, 6);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 320,
      ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });

    this.refreshGhost();
  }

  private tryRemoveAt(worldX: number, worldY: number) {
    const cell = this.roof.worldToCell(worldX, worldY);
    if (!cell) return;
    const idx = this.placed.findIndex((p) => {
      for (let dy = 0; dy < p.panel.spanH(); dy++) {
        for (let dx = 0; dx < p.panel.spanW(); dx++) {
          if (p.panel.cellX + dx === cell.x && p.panel.cellY + dy === cell.y) return true;
        }
      }
      return false;
    });
    if (idx === -1) return;
    const [removed] = this.placed.splice(idx, 1);
    removed.sprite.destroy();
    this.remaining[removed.panel.type.id]++;
    this.hud.update(this.computeState());
    this.redrawWires();
  }

  private occupiedSet(): Set<string> {
    const s = new Set<string>();
    for (const p of this.placed) for (const c of panelOccupiedCells(p.panel)) s.add(c);
    return s;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Wires — draw amber strands from each panel to the nearest inverter, then
  // from each inverter to the nearest battery (visual system diagram).
  // ────────────────────────────────────────────────────────────────────────────
  private redrawWires() {
    if (!this.wireGfx) return;
    this.wireGfx.clear();

    const inverters = this.placed.filter((p) => isInverter(p.panel.type));
    const batteries = this.placed.filter((p) => isBattery(p.panel.type));
    const panels = this.placed.filter((p) => isPanel(p.panel.type));
    if (inverters.length === 0) return;

    const centerOf = (p: PanelInstance) => ({
      x: this.roof.origin.x + (p.cellX + p.spanW() / 2) * GAME.cell,
      y: this.roof.origin.y + (p.cellY + p.spanH() / 2) * GAME.cell,
    });

    const nearestInverter = (p: PanelInstance) => {
      const here = centerOf(p);
      let best = inverters[0];
      let bestD = Infinity;
      for (const inv of inverters) {
        const c = centerOf(inv.panel);
        const d = (c.x - here.x) ** 2 + (c.y - here.y) ** 2;
        if (d < bestD) {
          best = inv;
          bestD = d;
        }
      }
      return best;
    };

    this.wireGfx.lineStyle(1.5, 0xfdbc1a, 0.55);
    for (const panel of panels) {
      const a = centerOf(panel.panel);
      const inv = nearestInverter(panel.panel);
      const b = centerOf(inv.panel);
      this.wireGfx.lineBetween(a.x, a.y, b.x, b.y);
    }
    this.wireGfx.lineStyle(2, 0x57c47a, 0.7);
    for (const bat of batteries) {
      const a = centerOf(bat.panel);
      const inv = nearestInverter(bat.panel);
      const b = centerOf(inv.panel);
      this.wireGfx.lineBetween(a.x, a.y, b.x, b.y);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Output calc
  // ────────────────────────────────────────────────────────────────────────────
  private computeState() {
    let panelW = 0;
    let invCapW = 0;
    let batCreditW = 0;
    let batsInSun = 0;
    let batsCount = 0;
    let invCount = 0;
    let spent = 0;
    let storageKWh = 0;
    let sunPenaltyTotal = 0;

    for (const p of this.placed) {
      const t = p.panel.type;
      spent += t.cost;
      if (isPanel(t)) {
        const m = this.roof.panelMultiplier(
          p.panel.cellX,
          p.panel.cellY,
          p.panel.spanW(),
          p.panel.spanH(),
        );
        panelW += (t.watts ?? 0) * m;
      } else if (isInverter(t)) {
        invCount++;
        invCapW += (t.capacityKW ?? 0) * 1000;
      } else if (isBattery(t)) {
        batsCount++;
        storageKWh += t.storageKWh ?? 0;
        // Continuous exposure-based credit + penalty.
        const exposure = this.roof.computeExposure(p.panel.cellX, p.panel.cellY);
        // Credit scales linearly from full (shade) to zero (sun).
        batCreditW += (t.bonusKW ?? 0) * 1000 * (1 - exposure);
        // Penalty fraction of total system output, applied per battery,
        // proportional to how directly sun-facing this spot is.
        if (exposure > 0.7) batsInSun++;
        sunPenaltyTotal += exposure * BATTERY_SUN_PENALTY;
      }
    }

    const cappedW = invCount === 0 ? 0 : Math.min(panelW, invCapW);
    let outputW = cappedW + batCreditW;
    // Clamp the summed penalty so stacking sunny batteries curves toward a
    // floor instead of falling off a cliff to zero output.
    if (sunPenaltyTotal > 0) outputW *= Math.max(0.1, 1 - Math.min(0.9, sunPenaltyTotal));
    outputW = Math.max(0, outputW);

    // Hidden trade pricing — 25% off everything when "JUICE" has been typed
    if (this.juiceMode) spent = Math.round(spent * 0.75);

    return {
      outputW,
      targetW: this.level.targetKW * 1000,
      storageKWh,
      targetKWh: this.level.targetKWh,
      spent,
      budget: this.level.budget,
      remaining: this.remaining,
      hasInverter: invCount > 0,
      panelRawW: panelW,
      invCapW,
      batCreditW,
      batsInSun,
      batsCount,
    };
  }

  private submit() {
    const s = this.computeState();
    const passInverter = s.hasInverter;
    const passOutput = s.outputW >= s.targetW;
    const passStorage = s.storageKWh >= s.targetKWh;
    const passBudget = s.spent <= s.budget;
    // Budget no longer gates winning — it gates star count.
    const win = passInverter && passOutput && passStorage;
    let stars = 0;
    if (win) {
      stars = 1;
      // 2nd star — under budget
      if (passBudget) stars++;
      // 3rd star — comfortable on both fronts
      if (passBudget && s.outputW >= s.targetW * 1.1 && s.spent <= s.budget * 0.9) {
        stars++;
      }
    }
    const elapsedMs = this.time.now - this.startedAt;
    this.scene.start('ResultScene', {
      levelIndex: this.levelIndex,
      win,
      stars,
      passOutput,
      passStorage,
      passBudget,
      passInverter,
      outputW: s.outputW,
      targetW: s.targetW,
      storageKWh: s.storageKWh,
      targetKWh: s.targetKWh,
      spent: s.spent,
      budget: s.budget,
      batsInSun: s.batsInSun,
      batCreditW: s.batCreditW,
      elapsedMs,
    });
  }
}
